import React, { createContext, useContext, useState, useCallback } from 'react';
import LoginApi, {
  jwtRoleToLoginType,
  parseLoginIdentity,
  type LoginResponse,
  type LoginType,
  type UserRole,
} from '../services/LoginApiClient';
import { clearParachuteAuthSession, setParachuteAuthToken } from '../utils/parachuteAuthToken';

export type { LoginType, UserRole };

/** Older sessions: long UI strings and pre–LoginType app state. Avoid fuzzy `.includes()` (false positives). */
function normalizeLegacyStoredLoginType(u: Record<string, unknown>): LoginType | null {
  const raw = u.loginType;
  if (raw === 'Root' || raw === 'Firm' || raw === 'Client') return raw as LoginType;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (s === 'Root') return 'Root';
    if (s === 'Firm' || s === 'FirmAdmin') return 'Firm';
    if (s === 'Client') return 'Client';
    if (s === 'Firm User Login (Internal)') return 'Firm';
    if (s === 'Client/Portal Login (External)') return 'Client';
  }
  if (u.adminScope === 'firm' || u.role === 'firm_admin') return 'Firm';
  const jwtRole = u.jwtRole;
  if (typeof jwtRole === 'string' && jwtRole.trim()) return jwtRoleToLoginType(jwtRole);
  return null;
}

function parseStoredUser(raw: string): User | null {
  try {
    const u = JSON.parse(raw) as Record<string, unknown>;
    if (!u || typeof u.email !== 'string') return null;
    const r = u.role === 'firm_admin' ? 'admin' : u.role;
    if (r !== 'admin' && r !== 'client') return null;

    const loginTypeResolved = normalizeLegacyStoredLoginType(u) ?? (r === 'admin' ? 'Root' : 'Client');
    const { role, loginType: lt } = parseLoginIdentity({
      id: String(u.id ?? ''),
      firstName: '',
      lastName: '',
      email: String(u.email),
      phone: '',
      isActive: true,
      loginType: loginTypeResolved,
      role: '',
    });

    const firstName = typeof u.firstName === 'string' ? u.firstName : '';
    const lastName = typeof u.lastName === 'string' ? u.lastName : '';
    const nameFromParts = [firstName, lastName]
      .map((s) => s.trim())
      .filter(Boolean)
      .join(' ');
    const name = typeof u.name === 'string' && u.name.trim() ? u.name.trim() : nameFromParts || u.email;

    const token = typeof u.token === 'string' ? u.token : undefined;
    const tenantId = typeof u.tenantId === 'string' ? u.tenantId : undefined;
    const jwtRole = typeof u.jwtRole === 'string' && u.jwtRole.trim() !== '' ? u.jwtRole.trim() : undefined;
    const companyName =
      typeof u.companyName === 'string' && u.companyName.trim() !== '' ? u.companyName.trim() : undefined;

    return {
      id: String(u.id ?? u.email),
      email: u.email,
      name,
      role,
      loginType: lt,
      tenantId,
      token,
      jwtRole,
      companyName,
    };
  } catch {
    return null;
  }
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  loginType: LoginType;
  /** For law-firm admin or client: tenant id. Platform root omits. */
  tenantId?: string;
  token?: string;
  /** Raw JWT role claim (e.g. Admin, Staff, Client). Used for firm workspace permissions. */
  jwtRole?: string;
  /** Linked company name, or law firm name (client portal header). From login API. */
  companyName?: string;
}

interface AuthContextValue {
  user: User | null;
  login: (email: string, password: string) => Promise<User>;
  completeLogin: (user: User) => void;
  logout: () => void;
  loginError: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState(() => {
    try {
      const saved = sessionStorage.getItem('parachute-user');
      if (saved) {
        const u = parseStoredUser(saved);
        if (u?.token) setParachuteAuthToken(u.token);
        return u;
      }
    } catch (_) {}
    return null as User | null;
  });
  const [loginError, setLoginError] = useState(null as string | null);

  const completeLogin = useCallback((u: User) => {
    setUser(u);
    sessionStorage.setItem('parachute-user', JSON.stringify(u));
    if (u.token) setParachuteAuthToken(u.token);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setLoginError(null);
    try {
      const res: LoginResponse = await LoginApi.login(email.trim(), password ?? '');
      const d = res.data;
      const name = [d.firstName, d.lastName].filter(Boolean).join(' ') || d.email;
      const { role, loginType } = parseLoginIdentity(d);
      const u: User = {
        id: d.id,
        email: d.email,
        name,
        role,
        loginType,
        tenantId: loginType === 'Client' || loginType === 'Firm' ? `tenant-${d.email}` : undefined,
        token: d.token,
        jwtRole: typeof d.role === 'string' && d.role.trim() !== '' ? d.role.trim() : undefined,
        companyName: d.companyName,
      };
      return u;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setLoginError(message);
      throw err;
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setLoginError(null);
    clearParachuteAuthSession();
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, completeLogin, logout, loginError }}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
